/* eslint-disable react-refresh/only-export-components */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// A request failed for a reason that is safe and worth auto-retrying:
// our backend returns 503 for transient DB connectivity loss (Neon compute
// resuming from autosuspend), and axios reports timeouts / dropped sockets as
// ECONNABORTED / ERR_NETWORK. These all happen *before* the server commits, so
// a retry is safe (no partial write). Anything else (4xx, 500) is a real error.
function isTransientError(error) {
  const status = error?.response?.status;
  const code = error?.code;
  return (
    status === 503 || code === "ECONNABORTED" || code === "ERR_NETWORK"
  );
}

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time before data is considered stale (5 minutes)
      staleTime: 5 * 60 * 1000,
      // Time before inactive queries are garbage collected (10 minutes)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests
      retry: 1,
      // Refetch on window focus in production
      refetchOnWindowFocus: import.meta.env.PROD,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Form submits are mutations. By default mutations don't retry, so a
      // transient stale-connection 503 surfaced a toast and forced the user to
      // resend. Auto-retry transient failures (up to 2x) instead — onError (and
      // its toast) only fires after retries are exhausted, so the happy path is
      // invisible to the user.
      retry: (failureCount, error) =>
        failureCount < 2 && isTransientError(error),
      // Backoff 1s → 2s → 4s (capped) to ride out Neon's ~1-2s wake-up latency.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    },
  },
});

/**
 * QueryProvider component that wraps the app with TanStack Query
 * Provides query client and dev tools
 */
export function QueryProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}

// Export the query client for use outside React components
export { queryClient };
